/**
 * Promotional banner offset
 *
 * A third-party app (for example a free-shipping bar) injects a `position: fixed`
 * banner pinned to the top of the viewport (`#fsb_background`). Left alone it overlaps
 * the header and the top of the page. This module measures the banner and exposes its
 * height as the `--fsb-banner-height` custom property on `<body>`, toggling the
 * `fsb-active` class so the theme can reserve space for it. The CSS that consumes both
 * lives in `snippets/promo-banner-offset.liquid`.
 *
 * The banner is injected asynchronously and can be dismissed or re-rendered, so the
 * body is observed for it being added or removed, and the banner itself for height
 * changes (its content wraps at narrow widths).
 *
 * Progressive enhancement: with no JavaScript nothing runs and the banner simply
 * overlays as the app renders it — the page remains fully usable.
 *
 * The app's banner id is the only integration-specific value; change it here if the
 * merchant swaps to a different promotional-bar app.
 */

const BANNER_SELECTOR = '#fsb_background';
const ACTIVE_CLASS = 'fsb-active';
const HEIGHT_PROPERTY = '--fsb-banner-height';

/** @type {ResizeObserver | null} */
let bannerResizeObserver = null;

/** @type {HTMLElement | null} */
let observedBanner = null;

let syncScheduled = false;

/**
 * Measures the banner and writes its height to `<body>`, or clears the offset when the
 * banner is absent, hidden, or already occupies space in the normal page flow. Runs at most once per frame via {@link scheduleSync}.
 */
const applyBannerHeight = () => {
  syncScheduled = false;

  const banner = document.querySelector(BANNER_SELECTOR);

  if (!(banner instanceof HTMLElement) || banner.offsetHeight === 0) {
    document.body.classList.remove(ACTIVE_CLASS);
    document.body.style.removeProperty(HEIGHT_PROPERTY);
    stopObservingBanner();
    return;
  }

  startObservingBanner(banner);

  // Only fixed banners need extra space. Relative, static and sticky banners
  // already occupy space in the page and an offset would create a blank gap.
  let fixedBanner = false;
  for (let element = banner; element && element !== document.body; element = element.parentElement) {
    if (getComputedStyle(element).position === 'fixed') {
      fixedBanner = true;
      break;
    }
  }

  if (!fixedBanner) {
    document.body.classList.remove(ACTIVE_CLASS);
    document.body.style.removeProperty(HEIGHT_PROPERTY);
    return;
  }

  // Round up so a sub-pixel banner height never leaves a 1px sliver of overlap.
  const height = Math.ceil(banner.getBoundingClientRect().height);
  document.body.style.setProperty(HEIGHT_PROPERTY, `${height}px`);
  document.body.classList.add(ACTIVE_CLASS);
};

/** Coalesces bursts of mutations and resizes into a single measurement per frame. */
const scheduleSync = () => {
  if (syncScheduled) return;

  syncScheduled = true;
  requestAnimationFrame(applyBannerHeight);
};

/**
 * Watches the banner for height changes.
 * @param {HTMLElement} banner - The promotional banner element.
 */
const startObservingBanner = (banner) => {
  if (observedBanner === banner) return;

  stopObservingBanner();
  bannerResizeObserver = new ResizeObserver(scheduleSync);
  bannerResizeObserver.observe(banner);
  observedBanner = banner;
};

/** Disconnects the banner height observer. */
const stopObservingBanner = () => {
  bannerResizeObserver?.disconnect();
  bannerResizeObserver = null;
  observedBanner = null;
};

const bodyObserver = new MutationObserver(scheduleSync);
bodyObserver.observe(document.body, { childList: true, subtree: true });

window.addEventListener('resize', scheduleSync);

scheduleSync();
